'use client';

import React from 'react';
import Layout from '../../components/Layout';
import Link from 'next/link';
import SubDocsLayout from '../../components/SubDocsLayout';
import useTranslation from '../../lib/translation';
import SEOHead from '../../components/SEOHead';
import { useLocalePath } from '../../lib/useLocalePath';

const Cookies = () => {
  const { t, lang } = useTranslation('common');
  const { prefix } = useLocalePath();
  const privacyHref = prefix ? `${prefix}/privacy-policy` : '/privacy-policy';

  return (
    <Layout>
      <SEOHead
        title={
          lang === 'lt' ? Array.isArray(t('cookiesInfo.pageTitle')) ? (t('cookiesInfo.pageTitle') as string[]).join(' ') : (t('cookiesInfo.pageTitle') as string) : 'Cookies | LB Websites'
        }
        description={
          lang === 'lt'
            ? Array.isArray(t('cookiesInfo.description')) ? (t('cookiesInfo.description') as string[]).join(' ') : (t('cookiesInfo.description') as string)
            : 'Learn how LB Websites uses cookies to comply with GDPR and improve user experience.'
        }
        keywords='cookies, privacy, GDPR, data protection, website cookies, cookie policy'
        canonicalUrl={
          lang === 'lt' ? '/lt/sub/slapukai' : '/sub/cookies-info'
        }
        lang={lang}
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name:
            lang === 'lt'
              ? Array.isArray(t('cookiesInfo.pageTitle')) ? (t('cookiesInfo.pageTitle') as string[]).join(' ') : (t('cookiesInfo.pageTitle') as string)
              : 'Cookies | LB Websites',
          description:
            lang === 'lt'
              ? Array.isArray(t('cookiesInfo.description')) ? (t('cookiesInfo.description') as string[]).join(' ') : (t('cookiesInfo.description') as string)
              : 'Learn how LB Websites uses cookies to comply with GDPR and improve user experience.',
          url: `https://www.lbvisible.com${
            lang === 'lt' ? '/lt/sub/slapukai' : '/sub/cookies-info'
          }`,
          inLanguage: lang === 'lt' ? 'lt' : 'en',
        }}
      />

      <SubDocsLayout>
        <h1 className='text-2xl sm:text-3xl font-semibold text-gray-900 dark:text-white mb-4 text-left'>
          {lang === 'lt' ? t('cookiesInfo.heading') : 'Cookie Usage'}
        </h1>
        <p className='text-base text-gray-600 dark:text-gray-300 mb-10 text-left leading-relaxed'>
            {lang === 'lt'
              ? t('cookiesInfo.subtitle')
              : 'Our website uses cookies to ensure functionality, improve your experience, and support optional analytics—only after your consent. This ensures compliance with GDPR and EU ePrivacy standards.'}
          </p>

          <ul className='space-y-6 mb-10'>
            <li className='flex gap-4'>
              <span className='flex-shrink-0 w-2 h-2 rounded-full mt-2 bg-gray-400 dark:bg-gray-500' />
              <div>
              <span className='font-semibold text-gray-900 dark:text-white'>
                {lang === 'lt'
                  ? t('cookiesInfo.types.essential.title')
                  : 'Essential Cookies:'}
              </span>{' '}
              {lang === 'lt'
                ? t('cookiesInfo.types.essential.desc')
                : 'Required for site functionality like navigation, forms, and login.'}
              </div>
            </li>
            <li className='flex gap-4'>
              <span className='flex-shrink-0 w-2 h-2 rounded-full mt-2 bg-gray-400 dark:bg-gray-500' />
              <div>
              <span className='font-semibold text-gray-900 dark:text-white'>
                {lang === 'lt'
                  ? t('cookiesInfo.types.analytics.title')
                  : 'Analytics Cookies:'}
              </span>{' '}
              {lang === 'lt'
                ? t('cookiesInfo.types.analytics.desc')
                : 'We use Google Analytics to understand user behavior—but only after consent is given.'}
              </div>
            </li>
            <li className='flex gap-4'>
              <span className='flex-shrink-0 w-2 h-2 rounded-full mt-2 bg-gray-400 dark:bg-gray-500' />
              <div>
              <span className='font-semibold text-gray-900 dark:text-white'>
                {lang === 'lt'
                  ? t('cookiesInfo.types.preference.title')
                  : 'Consent-Based:'}
              </span>{' '}
              {lang === 'lt'
                ? t('cookiesInfo.management.desc')
                : 'Users can accept or decline non-essential cookies through our consent banner. Preferences can be updated by clearing cookies.'}
              </div>
            </li>
            <li className='flex gap-4'>
              <span className='flex-shrink-0 w-2 h-2 rounded-full mt-2 bg-gray-400 dark:bg-gray-500' />
              <div>
              <span className='font-semibold text-gray-900 dark:text-white'>
                {lang === 'lt' ? 'Duomenų privatumas:' : 'Data Privacy:'}
              </span>{' '}
              {lang === 'lt'
                ? 'Nesaugome asmeninės informacijos be jūsų sutikimo. Analitikos duomenys yra anoniminiai ir naudojami našumui pagerinti.'
                : 'We do not store personal information without your consent. Analytics data is anonymous and used to improve performance.'}
              </div>
            </li>
          </ul>

          <p className='text-sm text-gray-500 dark:text-gray-400 text-left'>
            {lang === 'lt'
              ? 'Toliau naudodami šią svetainę, sutinkate su būtinų slapukų naudojimu. Daugiau informacijos rasite mūsų '
              : 'By continuing to use this website, you agree to essential cookie use. For more details, see our '}
            <Link
              href={privacyHref}
              className='text-gray-700 dark:text-gray-200 font-medium underline hover:no-underline'
            >
              {lang === 'lt' ? t('footer.privacyPolicy') : 'Privacy Policy'}
            </Link>
            .
          </p>
      </SubDocsLayout>
    </Layout>
  );
};

export default Cookies;
